import { format, addYears } from 'date-fns';
import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { bindActionCreators } from 'redux';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { FiChevronDown } from 'react-icons/fi';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { replace } from 'react-router-redux';
import { compose, withHandlers, withProps, withStateHandlers } from 'recompose';
import queryString from 'query-string';
import { dateFormats, riskThresholds, routes } from '../../../constants';
import exportSingleTeamCpdReportPdf from '../../../reports/export-single-team-cpd-report-pdf';
import exportTeamCpdReportPdf from '../../../reports/export-team-cpd-report-pdf';
import { startOfNextCpdPeriod, startOfThisCpdPeriod } from '../../../services';
import { noCapMap, unfixedFloor, unfixedCeil } from '../../../utils';
import { Icon, SortableTable, SelectFormField } from '../../components';
import { ProgressChart } from '../../partials';
import DownloadBusinessActivityButtonForBusiness from './download-business-activity-button-for-business';
import DownloadBusinessActivityButtonForAdmin from '../../pages/admin-portal/user-portal/partial/download-business-activity-button-for-admin';

const formatDate = (date) => format(new Date(date), dateFormats.LongDate);

const mapRiskRate = (riskRate) =>
  riskRate < riskThresholds.medium
    ? 'Low'
    : riskRate < riskThresholds.high
    ? 'Medium'
    : 'High';

const mapRiskRateStyle = (riskRate) =>
  !riskRate
    ? 'riskRating'
    : riskRate === 'Low'
    ? 'low-risk'
    : riskRate === 'Medium'
    ? 'medium-risk'
    : 'high-risk';

const TotalTeamProgressColumns = [
  {
    name: 'nameForDisplay',
    display: () => 'Team',
    className: () => 'team'
  },
  {
    name: 'teamSize',
    display: () => 'Team size',
    className: () => ''
  },
  {
    name: 'trainingHoursMet',
    display: () => 'Training hrs met',
    className: () => ''
  },
  {
    name: 'totalCpdPoints',
    display: () => 'Training plan hours',
    className: () => ''
  },
  {
    name: 'totalProgressHours',
    display: () => 'Completed hours',
    className: () => ''
  },
  {
    name: 'shortFallHours',
    display: () => 'Shortfall hours',
    className: () => ''
  },
  {
    name: 'totalProgressPercentage',
    display: () => 'Total progress %',
    className: () => ''
  },
  {
    name: 'shortFall',
    display: () => 'Shortfall %',
    className: () => ''
  },
  {
    name: 'daysLeft',
    display: () => 'Days left',
    className: () => ''
  },
  {
    name: 'riskRating',
    display: () => 'Risk rating',
    className: mapRiskRateStyle
  }
];

const mapTeamProgressRow = ({
  team: { name },
  stats: {
    teamSize,
    totalProgressPercentage,
    shortFall,
    totalRiskRating,
    totalCpdPoints,
    totalApprovedCpdPointsCapped,
    totalPendingCpdPoints,
    totalTasaCpdPoints,
    averageCpdRemainingDays,
    cpdLastActivityDays
  },
  className
}) => ({
  name: `${he.decode(name)}`,
  nameForDisplay: `${he.decode(name)}`,
  teamSize: `${teamSize}`,
  trainingHoursMet: totalProgressPercentage >= 100 ? 'Yes' : 'No',
  totalCpdPoints: teamSize > 0 ? `${unfixedFloor(totalCpdPoints, 2)}` : '-',
  totalProgressPercentage:
    teamSize > 0 ? `${unfixedFloor(totalProgressPercentage, 1)}%` : '-',
  totalProgressHours:
    teamSize > 0 ? `${unfixedFloor(totalApprovedCpdPointsCapped, 2)}` : '-',
  shortFall:
    teamSize > 0 ? `${unfixedCeil(100 - totalProgressPercentage, 1)}%` : '-',
  shortFallHours: teamSize > 0 ? `${unfixedCeil(shortFall, 2)}` : '-',
  daysLeft:
    teamSize > 0
      ? `${unfixedCeil(Math.max(averageCpdRemainingDays, 0), 2)}`
      : '-',
  riskRating: teamSize > 0 ? mapRiskRate(totalRiskRating) : '-',
  className
});

const mapTotalTeamProgressRows = (
  industryProgressLabel,
  allMembersStats,
  unassignedMembersStats,
  businessTeams
) => {
  const defaultProgressTeamMembers = businessTeams.concat(
    unassignedMembersStats.teamSize === 0
      ? []
      : [
          {
            team: {
              name: 'Unassigned members'
            },
            stats: {
              teamSize: unassignedMembersStats.teamSize,
              trainingHoursMet:
                unassignedMembersStats.totalProgressPercentage >= 100
                  ? 'Yes'
                  : 'No',
              totalProgressPercentage:
                unassignedMembersStats.totalProgressPercentage,
              shortFall: unassignedMembersStats.shortFall,
              totalRiskRating: unassignedMembersStats.totalRiskRating,
              totalCpdPoints: unassignedMembersStats.totalCpdPoints,
              totalApprovedCpdPointsCapped:
                unassignedMembersStats.totalApprovedCpdPointsCapped,
              totalPendingCpdPoints:
                unassignedMembersStats.totalPendingCpdPoints,
              totalTasaCpdPoints: unassignedMembersStats.totalTasaCpdPoints,
              averageCpdRemainingDays: Math.max(
                unassignedMembersStats.averageCpdRemainingDays,
                0
              ),
              cpdLastActivityDays: unassignedMembersStats.cpdLastActivityDays
            }
          }
        ]
  );
  const defaultProgressRows = [
    {
      team: {
        name: industryProgressLabel
      },
      stats: {
        teamSize: allMembersStats.teamSize,
        trainingHoursMet:
          allMembersStats.totalProgressPercentage >= 100 ? 'Yes' : 'No',
        totalProgressPercentage: allMembersStats.totalProgressPercentage,
        shortFall: allMembersStats.shortFall,
        totalRiskRating: allMembersStats.totalRiskRating,
        totalCpdPoints: allMembersStats.totalCpdPoints,
        totalApprovedCpdPointsCapped:
          allMembersStats.totalApprovedCpdPointsCapped,
        totalPendingCpdPoints: allMembersStats.totalPendingCpdPoints,
        totalTasaCpdPoints: allMembersStats.totalTasaCpdPoints,
        averageCpdRemainingDays: Math.max(
          allMembersStats.averageCpdRemainingDays,
          0
        ),
        cpdLastActivityDays: allMembersStats.cpdLastActivityDays
      },
      className: 'heavy',
      members: defaultProgressTeamMembers
    }
  ];
  const progressGoalRows = fp.flatMap(
    ({ goal, completed, total, shortFall }) => {
      const progressGoalMembersRows = [
        ...fp.flow(
          fp.flatMap(({ goalProgress, team, stats }) => {
            return goalProgress
              .filter((progress) => progress.goal.id === goal.id)
              .map((progress) => {
                return {
                  team: {
                    name: team.name
                  },
                  stats: {
                    teamSize: stats.teamSize,
                    trainingHoursMet: progress.shortFall === 0 ? 'Yes' : 'No',
                    totalProgressPercentage: unfixedFloor(
                      100 * (progress.completed / progress.total),
                      2
                    ),
                    shortFall: unfixedCeil(progress.shortFall, 2),
                    totalRiskRating:
                      progress.shortFall /
                      Math.max(1, stats.averageCpdRemainingDays),
                    totalCpdPoints: unfixedFloor(progress.total, 2),
                    totalApprovedCpdPointsCapped: unfixedFloor(
                      progress.completed,
                      2
                    ),
                    totalPendingCpdPoints: 0,
                    totalTasaCpdPoints: 0,
                    averageCpdRemainingDays: Math.max(
                      0,
                      stats.averageCpdRemainingDays
                    ),
                    cpdLastActivityDays: stats.cpdLastActivityDays
                  }
                };
              });
          })
        )(businessTeams),
        ...fp.flow(
          fp.filter((progress) => progress.goal.id === goal.id),
          fp.map((progress) => ({
            team: {
              name: 'Unassigned members'
            },
            stats: {
              teamSize: unassignedMembersStats.teamSize,
              trainingHoursMet: progress.shortFall === 0 ? 'Yes' : 'No',
              totalProgressPercentage: unfixedFloor(
                100 * (progress.completed / progress.total),
                2
              ),
              shortFall: unfixedCeil(progress.shortFall, 2),
              totalRiskRating:
                progress.shortFall /
                Math.max(1, unassignedMembersStats.averageCpdRemainingDays),
              totalCpdPoints: unfixedFloor(progress.total, 2),
              totalApprovedCpdPointsCapped: unfixedFloor(progress.completed, 2),
              totalPendingCpdPoints: 0,
              totalTasaCpdPoints: 0,
              averageCpdRemainingDays: Math.max(
                0,
                unassignedMembersStats.averageCpdRemainingDays
              ),
              cpdLastActivityDays: unassignedMembersStats.cpdLastActivityDays
            }
          }))
        )(unassignedMembersStats.goals)
      ];
      return [
        {
          team: {
            name: goal.name
          },
          stats: {
            teamSize: allMembersStats.teamSize,
            trainingHoursMet: shortFall === 0 ? 'Yes' : 'No',
            totalProgressPercentage: unfixedFloor(100 * (completed / total), 2),
            shortFall: unfixedCeil(shortFall, 2),
            totalRiskRating:
              shortFall / Math.max(1, allMembersStats.averageCpdRemainingDays),
            totalCpdPoints: unfixedFloor(total, 2),
            totalApprovedCpdPointsCapped: unfixedFloor(completed, 2),
            totalPendingCpdPoints: 0,
            totalTasaCpdPoints: 0,
            averageCpdRemainingDays: Math.max(
              0,
              allMembersStats.averageCpdRemainingDays
            ),
            cpdLastActivityDays: allMembersStats.cpdLastActivityDays
          },
          className: 'heavy',
          members: progressGoalMembersRows
        }
      ];
    }
  )(allMembersStats.goals);

  return fp.flow(
    fp.map((row) => ({
      ...mapTeamProgressRow(row),
      members: fp.map(mapTeamProgressRow)(row.members)
    }))
  )(defaultProgressRows.concat(progressGoalRows));
};

const mapTeamProgressRows = (industryProgressLabel, businessTeams) =>
  noCapMap(({ stats, team, members, trainingManagers }) => {
    const teamProgressRow = [
      {
        name: industryProgressLabel,
        nameForDisplay: industryProgressLabel,
        trainingHoursMet: stats.totalProgressPercentage >= 100 ? 'Yes' : 'No',
        totalCpdPoints: `${stats.totalCpdPoints}`,
        totalProgressPercentage: `${unfixedFloor(
          stats.totalProgressPercentage,
          1
        )}%`,
        totalProgressHours: `${unfixedFloor(
          stats.totalApprovedCpdPointsCapped,
          2
        )}`,
        shortFall: `${unfixedCeil(100 - stats.totalProgressPercentage, 1)}%`,
        shortFallHours: `${unfixedCeil(stats.shortFall, 2)}`,
        daysLeft: `${unfixedCeil(
          Math.max(0, stats.averageCpdRemainingDays),
          2
        )}`,
        riskRating: mapRiskRate(stats.totalRiskRating),
        className: 'heavy',
        members: fp.flow(
          fp.filter(({ userId }) => !fp.includes(userId)(trainingManagers)),
          fp.map(
            ({
              userId,
              firstName,
              lastName,
              derivedStat: {
                progress,
                riskRating,
                shortFall,
                shortFallPercentage
              },
              primaryStat: {
                cpdRemainingDays,
                goal,
                pendingCpd,
                tasaCpd,
                validCpd,
                cpdLastActivityDays
              }
            }) => ({
              name: `${firstName} ${lastName}`,
              nameForDisplay: (
                <Link
                  to={{
                    pathname: routes.app.teams.allTeamMemberReport(userId),
                    search: window.location.search
                  }}
                  target='_blank'
                >
                  {`${firstName} ${lastName}`}
                </Link>
              ),
              trainingHoursMet: progress >= 100 ? 'Yes' : 'No',
              totalCpdPoints: `${goal}`,
              totalProgressPercentage: `${unfixedFloor(progress, 1)}%`,
              totalProgressHours: `${unfixedFloor(validCpd, 2)}`,
              shortFall: `${unfixedCeil(shortFallPercentage, 1)}%`,
              shortFallHours: `${unfixedCeil(shortFall, 2)}`,
              daysLeft: `${Math.max(cpdRemainingDays, 0)}`,
              riskRating: mapRiskRate(riskRating)
            })
          )
        )(members)
      }
    ];
    const progressGoalRows = fp.flatMap(
      ({ goal, completed, total, shortFall }) => {
        const progressPercentage = (100 * completed) / total;
        const shortFallPercentage = (100 * shortFall) / total;
        const riskRating =
          shortFall / Math.max(1, stats.averageCpdRemainingDays);
        const goalMembers = fp.flow(
          fp.map(({ goals, ...member }) => {
            const memberGoal = fp.find(
              (goalStat) => goalStat.goal.id === goal.id
            )(goals);
            return {
              member,
              memberGoal
            };
          }),
          fp.filter(({ memberGoal }) => memberGoal)
        )(members);
        return [
          {
            name: goal.name,
            nameForDisplay: goal.name,
            trainingHoursMet: shortFall === 0 ? 'Yes' : 'No',
            totalCpdPoints: `${total}`,
            totalProgressPercentage: `${unfixedFloor(progressPercentage, 1)}%`,
            totalProgressHours: `${unfixedFloor(completed, 2)}`,
            shortFall: `${unfixedCeil(shortFallPercentage, 1)}%`,
            shortFallHours: `${unfixedCeil(shortFall, 2)}`,
            daysLeft: `${unfixedCeil(
              Math.max(stats.averageCpdRemainingDays, 0),
              2
            )}`,
            riskRating: mapRiskRate(riskRating),
            className: 'heavy',
            members: fp.map(({ member, memberGoal }) => {
              const progress = unfixedFloor(
                (100 * memberGoal.completed) / memberGoal.total,
                2
              );
              const shortFallPercentage = unfixedFloor(
                (100 * memberGoal.shortFall) / memberGoal.total,
                2
              );
              const riskRating =
                memberGoal.shortFall / member.primaryStat.cpdRemainingDays;
              return {
                name: `${member.firstName} ${member.lastName}`,
                nameForDisplay: (
                  <Link
                    to={{
                      pathname: routes.app.teams.allTeamMemberReport(
                        member.userId
                      ),
                      search: window.location.search
                    }}
                    target='_blank'
                  >
                    {`${member.firstName} ${member.lastName}`}
                  </Link>
                ),
                trainingHoursMet: progress >= 100 ? 'Yes' : 'No',
                totalCpdPoints: `${memberGoal.total}`,
                totalProgressPercentage: `${unfixedFloor(progress, 1)}%`,
                totalProgressHours: `${unfixedFloor(memberGoal.completed, 2)}`,
                shortFall: `${unfixedCeil(shortFallPercentage, 1)}%`,
                shortFallHours: `${unfixedCeil(memberGoal.shortFall, 2)}`,
                daysLeft: `${Math.max(member.primaryStat.cpdRemainingDays, 0)}`,
                riskRating: mapRiskRate(riskRating)
              };
            })(goalMembers)
          }
        ];
      }
    )(stats.goals);

    return {
      teamProgress: stats.totalProgressPercentage,
      teamName: he.decode(team.name),
      goals: stats.goals,
      membersStatsRows: teamProgressRow.concat(progressGoalRows)
    };
  })(fp.filter(({ stats: { teamSize } }) => teamSize > 0)(businessTeams));

const TeamProgressReport = ({
  afsl,
  business,
  businessTeamsWithStats: { allMembersStats },
  filter,
  exportPdf,
  totalTeamProgressRows,
  teamProgressColumns,
  TeamsReportStats,
  reportBreadCrumb,
  teamId,
  defaultProgressLabel,
  showCpdPeriodSelect,
  yearsFromNow,
  goTo,
  trainingPlans,
  isBusinessReport,
  downloadBusinessActivityFor
}) => {
  const buttonComponents = {
    adminWithYearsOffset: DownloadBusinessActivityButtonForAdmin,
    businessWithYearsOffset: DownloadBusinessActivityButtonForBusiness
  };

  const DownloadBusinessActivitiesButton =
    buttonComponents[downloadBusinessActivityFor];
  return (
    <div className='report-layout'>
      <section
        id='header--report'
        key='report-header'
        className='layout--header'
      >
        <Grid>
          <Row className='sm-gutter'>
            <Col xs={12} md={5}>
              {reportBreadCrumb}
            </Col>
          </Row>
          <Row className='button-gutter'>
            <Col xs={12} md={2} mdOffset={isBusinessReport ? 8 : 10}>
              <Button
                className='large full pull-down-single'
                onClick={exportPdf}
              >
                <Icon
                  iconSet='glyphicon'
                  icon='download-alt'
                  className='space-right'
                />
                Download as PDF
              </Button>
            </Col>
            {isBusinessReport ? (
              <Col xs={12} md={2}>
                <DownloadBusinessActivitiesButton
                  business={business}
                  trainingPlans={trainingPlans}
                  yearsFromNow={yearsFromNow}
                  downloadFor={downloadBusinessActivityFor}
                  buttonText='download as csv'
                  downloadPeriodText={`${format(
                    filter.fromDate,
                    dateFormats.FilteringDate
                  )}_to_${format(
                    filter.toDate,
                    dateFormats.FilteringDate
                  )}`}
                />
              </Col>
            ) : null}
          </Row>
          {showCpdPeriodSelect ? (
            <Row className='button-gutter'>
              <Col xs={12} md={4} mdOffset={8}>
                <form>
                  <SelectFormField
                    emptyOption={false}
                    label='Choose report period'
                    value={yearsFromNow}
                    onChange={(e) => {
                      const newYearsFromNow = parseInt(e.target.value, 10);
                      const newSearchParam = queryString.stringify({
                        yearsFromNow: newYearsFromNow
                      });
                      goTo({ search: newSearchParam });
                    }}
                  >
                    <option key={0} value={0}>
                      Current CPD year&nbsp; (
                      {format(
                        addYears(filter.fromDate, -yearsFromNow),
                        dateFormats.ShortDate
                      )}
                      &nbsp; -{' '}
                      {format(
                        addYears(filter.toDate, -yearsFromNow),
                        dateFormats.ShortDate
                      )}
                      )
                    </option>
                    <option key={1} value={-1}>
                      Last CPD year&nbsp; (
                      {format(
                        addYears(filter.fromDate, -(1 + yearsFromNow)),
                        dateFormats.ShortDate
                      )}
                      &nbsp; -{' '}
                      {format(
                        addYears(filter.toDate, -(1 + yearsFromNow)),
                        dateFormats.ShortDate
                      )}
                      )
                    </option>
                  </SelectFormField>
                </form>
              </Col>
            </Row>
          ) : null}
          {!isBusinessReport ? null : (
            <Row className='no-gutter'>
              <Col xs={12} md={8} mdOffset={0}>
                <h1>{he.decode(business.name)} Team Summary</h1>
              </Col>
              <Col xs={12} md={4}>
                <p>
                  Team Activity Report: {formatDate(filter.fromDate)} -{' '}
                  {formatDate(filter.toDate)}
                </p>
                <p>AFSL: {afsl}</p>
              </Col>
              <Col xs={12} className='flexbox'>
                <div className='right-gap'>
                  <h3 className='text-center no-margin'>
                    {defaultProgressLabel}
                  </h3>
                  <div className='chart'>
                    <ProgressChart
                      progressPercentage={unfixedFloor(
                        allMembersStats.totalProgressPercentage,
                        1
                      )}
                      width={100}
                      height={100}
                    />
                  </div>
                </div>
                {fp.map(({ goal, total, completed }) => {
                  const progress = 100 * (completed / total);
                  return (
                    <div
                      key={goal.id}
                      className='flexbox vertical align-center right-gap'
                    >
                      <h3 className='text-center no-margin'>{goal.name}</h3>
                      <div className='chart'>
                        <ProgressChart
                          progressPercentage={progress}
                          width={100}
                          height={100}
                        />
                      </div>
                    </div>
                  );
                })(allMembersStats.goals)}
              </Col>
            </Row>
          )}
        </Grid>
      </section>
      {!isBusinessReport ? null : (
        <section
          id='cpd-progress-report'
          className='layout--section report-table-section'
        >
          <div className='container'>
            <TotalTeamProgressReport
              className='table--progress-report'
              rows={totalTeamProgressRows}
              columns={TotalTeamProgressColumns}
            />
          </div>
        </section>
      )}
      {noCapMap(
        ({ teamProgress, teamName, membersStatsRows, goals }, index) => (
          <SingleTeamProgressReport
            teamProgress={teamProgress}
            goals={goals}
            defaultProgressLabel={defaultProgressLabel}
            teamName={teamName}
            rows={membersStatsRows}
            columns={teamProgressColumns}
            key={index}
          />
        )
      )(TeamsReportStats)}
    </div>
  );
};

const TotalTeamProgressReport = compose(
  withStateHandlers(
    {
      showGoalsProgress: []
    },
    {
      toggleGoalProgress:
        ({ showGoalsProgress }) =>
        (indexToToggle) => ({
          showGoalsProgress: fp.includes(indexToToggle)(showGoalsProgress)
            ? fp.filter((indexToShow) => indexToShow !== indexToToggle)(
                showGoalsProgress
              )
            : showGoalsProgress.concat([indexToToggle])
        })
    }
  ),
  withProps(({ rows, showGoalsProgress, toggleGoalProgress }) => ({
    rows: fp.flow(
      noCapMap((row, rowId) => {
        const showMembers = fp.includes(rowId)(showGoalsProgress);
        const members = showMembers ? row.members : [];
        const nameForDisplay = (
          <div
            className='flexbox align-center'
            onClick={() => toggleGoalProgress(rowId)}
          >
            {row.nameForDisplay}
            <FiChevronDown className='left-gap icon-green' />
          </div>
        );
        return [
          {
            ...row,
            nameForDisplay
          }
        ].concat(members);
      }),
      fp.flatten
    )(rows)
  }))
)(({ rows, columns, className }) => (
  <SortableTable className={className} rows={rows} columns={columns} />
));

const SingleTeamProgressReport = compose(
  withStateHandlers(
    {
      showGoalsProgress: []
    },
    {
      toggleGoalProgress:
        ({ showGoalsProgress }) =>
        (indexToToggle) => ({
          showGoalsProgress: fp.includes(indexToToggle)(showGoalsProgress)
            ? fp.filter((indexToShow) => indexToShow !== indexToToggle)(
                showGoalsProgress
              )
            : showGoalsProgress.concat([indexToToggle])
        })
    }
  ),
  withProps(({ rows, showGoalsProgress, toggleGoalProgress }) => ({
    rows: fp.flow(
      noCapMap((row, rowId) => {
        const showMembers = fp.includes(rowId)(showGoalsProgress);
        const members = showMembers ? row.members : [];
        const nameForDisplay = (
          <div
            className='flexbox align-center'
            onClick={() => toggleGoalProgress(rowId)}
          >
            {row.nameForDisplay}
            <FiChevronDown className='left-gap icon-green' />
          </div>
        );
        return [
          {
            ...row,
            nameForDisplay
          }
        ].concat(members);
      }),
      fp.flatten
    )(rows)
  }))
)(({ teamProgress, goals, defaultProgressLabel, teamName, rows, columns }) => (
  <div>
    <section id='header--report' className='layout--header'>
      <Grid>
        <Row>
          <Col xs={12} md={6} mdOffset={3}>
            <h1> {he.decode(teamName)} Total CPD Progress</h1>
          </Col>
          <Col xs={12} md={3}></Col>
          <Col xs={12} className='flexbox'>
            <div className='right-gap'>
              <h3 className='text-center no-margin'>{defaultProgressLabel}</h3>
              <div className='chart'>
                <ProgressChart
                  progressPercentage={unfixedFloor(teamProgress, 1)}
                  width={100}
                  height={100}
                />
              </div>
            </div>
            {fp.map(({ goal, total, completed }) => {
              const progress = 100 * (completed / total);
              return (
                <div
                  key={goal.id}
                  className='flexbox vertical align-center right-gap'
                >
                  <h3 className='text-center no-margin'>{goal.name}</h3>
                  <div className='chart'>
                    <ProgressChart
                      progressPercentage={progress}
                      width={100}
                      height={100}
                    />
                  </div>
                </div>
              );
            })(goals)}
          </Col>
        </Row>
      </Grid>
    </section>
    <section className='report-table-section'>
      <div className='container'>
        <SortableTable
          className='table--progress-report'
          rows={rows}
          columns={columns}
        />
      </div>
    </section>
  </div>
));

const mapStateToProps = (state) => ({
  industryProgressLabel: fp.flow(
    fp.get('trainingPlans'),
    fp.first,
    fp.getOr('CPD Progress')('industry.defaultProgressLabel')
  )(state.user.profile)
});

const mapDispatchToProps = (dispatch) => ({
  goTo: bindActionCreators(replace, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(({ match }) => {
    const hasMatch = !!(match && match.params);
    const teamId = hasMatch ? match.params.teamId : null;
    const isBusinessReport = hasMatch && match.params.teamId ? false : true;

    return {
      teamId,
      isBusinessReport
    };
  }),
  withProps(
    ({
      teamId,
      isBusinessReport,
      businessTeamsWithStats: {
        allMembersStats,
        unassignedMembersStats,
        unassignedMembersCpdCategorys,
        businessTeams
      },
      industryProgressLabel
    }) => ({
      filter: {
        fromDate: startOfThisCpdPeriod(allMembersStats.averageCpdRemainingDays),
        toDate: startOfNextCpdPeriod(allMembersStats.averageCpdRemainingDays)
      },
      totalTeamProgressRows: mapTotalTeamProgressRows(
        industryProgressLabel,
        allMembersStats,
        unassignedMembersStats,
        businessTeams
      ),
      teamProgressColumns: fp.filter((col) => col.name !== 'teamSize')(
        TotalTeamProgressColumns
      ),
      TeamsReportStats: mapTeamProgressRows(
        industryProgressLabel,
        fp.filter(({ team }) =>
          !isBusinessReport ? parseInt(teamId, 10) === team.id : true
        )(
          businessTeams.concat(
            unassignedMembersCpdCategorys.length > 0
              ? [
                  {
                    team: {
                      name: 'Unassigned Members'
                    },
                    stats: unassignedMembersStats,
                    members: unassignedMembersCpdCategorys
                  }
                ]
              : []
          )
        )
      )
    })
  ),
  withProps(({ afsLicence }) => ({
    afsl: afsLicence
      ? `${afsLicence.afsLicenceHolder} - (${afsLicence.afsLicenceNumber})`
      : null
  })),
  withHandlers({
    exportPdf:
      ({
        business,
        afsl,
        businessTeamsWithStats,
        totalTeamProgressRows,
        TeamsReportStats,
        teamId,
        filter,
        defaultProgressLabel
      }) =>
      (e) => {
        const teamsReportStatsExpanded = fp.map((teamStat) => {
          return {
            ...teamStat,
            membersStatsRows: fp.flatMap((memberStatRow) =>
              [memberStatRow].concat(memberStatRow.members)
            )(teamStat.membersStatsRows)
          };
        })(TeamsReportStats);
        const totalTeamProgressRowsExtended = fp.flatMap((row) => [
          row,
          ...row.members
        ])(totalTeamProgressRows);
        if (teamId) {
          exportSingleTeamCpdReportPdf({
            afsl,
            business,
            businessTeamsWithStats,
            totalTeamProgressRows: totalTeamProgressRowsExtended,
            TeamsReportStats: teamsReportStatsExpanded,
            defaultProgressLabel
          });
        } else {
          exportTeamCpdReportPdf({
            afsl,
            business,
            businessTeamsWithStats,
            totalTeamProgressRows: totalTeamProgressRowsExtended,
            TeamsReportStats: teamsReportStatsExpanded,
            filter,
            defaultProgressLabel
          });
        }
      }
  })
)(TeamProgressReport);



// WEBPACK FOOTER //
// ./src/containers/pages/shared/team-progress-report.js